import type { SubscriptionTier } from "@/shared/lib/supabase/types"

/** The fixed reason chips in the chat feedback form. Analytics keeps only these values. */
export const MESSAGE_FEEDBACK_CATEGORIES = [
  "Off-topic",
  "Out of character",
  "Repetitive",
  "Boring",
  "Inappropriate",
  "Factually wrong",
] as const

export const MESSAGE_FEEDBACK_NORMALIZED_CATEGORIES = [
  "freshness_repetition",
  "persona_quality",
  "safety",
  "factuality",
  "other",
] as const

export type MessageFeedbackCategory = (typeof MESSAGE_FEEDBACK_NORMALIZED_CATEGORIES)[number]

export type CancelFeedbackCategory =
  | "engagement_decay"
  | "checkout_clarity"
  | "price_value"
  | "technical_issue"
  | "product_gap"
  | "other"

export type BillingPeriod = "monthly" | "yearly" | "unknown"

const ANNUAL_DISCOUNT_PERCENT = 60
// Inferred analytics snapshot; keep in sync with src/features/credits/constants.ts
// and Stripe pricing whenever subscription tier prices change.
const MONTHLY_PRICE_BY_TIER: Record<Exclude<SubscriptionTier, "free">, number> = {
  starter: 12.99,
  premium: 27.99,
  ultimate: 49.99,
}

function getYearlyAmountCents(monthlyPrice: number): number {
  const yearlyMonthlyPrice = monthlyPrice * (1 - ANNUAL_DISCOUNT_PERCENT / 100)
  return Math.round(yearlyMonthlyPrice * 12 * 100)
}

export function normalizeMessageFeedbackCategory(
  category: string
): MessageFeedbackCategory {
  const normalized = category.trim().toLowerCase()
  if (normalized === "repetitive" || normalized === "boring") {
    return "freshness_repetition"
  }
  if (normalized === "off-topic" || normalized === "out of character") {
    return "persona_quality"
  }
  if (normalized === "inappropriate") return "safety"
  if (normalized === "factually wrong") return "factuality"
  return "other"
}

export function hasNegativeFreshnessFeedback(categories: string[]): boolean {
  return categories.some(
    (category) => normalizeMessageFeedbackCategory(category) === "freshness_repetition"
  )
}

export function normalizeCancelFeedbackCategory(feedback: string): CancelFeedbackCategory {
  const normalized = feedback.trim().toLowerCase()

  if (/\b(boring|bored|repetitive|repeat|same|stale|fresh|variety)\b/.test(normalized)) {
    return "engagement_decay"
  }
  if (/\b(accident\w*|confus|did not mean|didn't mean|trial)\b/.test(normalized)) {
    return "checkout_clarity"
  }
  if (/\b(price|cost|expensive|money|refund|value|worth)\b/.test(normalized)) {
    return "price_value"
  }
  if (/\b(bug|error|broken|slow|crash|loading|technical)\b/.test(normalized)) {
    return "technical_issue"
  }
  if (/\b(missing|feature|need|want|wish|more)\b/.test(normalized)) {
    return "product_gap"
  }

  return "other"
}

export function normalizeBillingPeriod(value: string | null | undefined): BillingPeriod {
  if (value === "monthly" || value === "yearly") return value
  if (value === "annual") return "yearly"
  return "unknown"
}

export function inferSubscriptionAmountCents(
  tier: SubscriptionTier,
  billingPeriod: BillingPeriod
): number | null {
  if (tier === "free" || billingPeriod === "unknown") return null

  const monthlyPrice = MONTHLY_PRICE_BY_TIER[tier]
  if (typeof monthlyPrice !== "number") return null

  return billingPeriod === "yearly"
    ? getYearlyAmountCents(monthlyPrice)
    : Math.round(monthlyPrice * 100)
}
